<script setup>
import { ref, onMounted, watch, onUnmounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from 'vue-i18n'

import { NInput, NButton, NSpin, NAlert, NPagination } from "naive-ui";
import ProviderCard from '../components/ProviderCard.vue';
import { fetchProviders } from "../services/provider";
import { createBookmark, deleteBookmark, fetchBookmarks } from '../services/bookmark';
import { isAuthenticated, currentUser, authToken } from '../services/auth';
import { dt } from '@/utils/i18n'

// Leaflet imports
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n()

// Reactive state
const providers = ref([]);
const loading = ref(false);
const error = ref(null);
const searchQuery = ref("");
const selectedLanguage = ref(
  localStorage.getItem("preferredLanguage") || "english"
);

// Pagination state
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);
const totalPages = ref(1);

const showModal = ref(false);
const editingProvider = ref(null);
const bookmarkedProviderIds = ref(new Set()); // Track bookmarked providers
const isLoadingBookmarks = ref(false);

// Map state
const mapContainer = ref(null);
const map = ref(null);
const markers = ref([]);
const markersGroup = ref(null);

// Initialize search query from URL parameters if they exist
onMounted(async () => {
  if (route.query.q) {
    searchQuery.value = route.query.q;
  }

  if (route.query.language) {
    selectedLanguage.value = route.query.language;
  }

  if (route.query.page) {
    currentPage.value = parseInt(route.query.page) || 1;
  }

  // Perform search with URL parameters
  await loadProviders();

  // Initialize map after providers are loaded
  await nextTick();
  initializeMap();

  // Load user's bookmarks if authenticated
  if (isAuthenticated.value) {
    await loadBookmarks();
  }

  // Add event listener for the app-language-updated event
  window.addEventListener('language-changed', handleLanguageChange);
  window.addEventListener('app-language-updated', handleAppLanguageUpdate);
});

// Clean up event listeners when component is unmounted
onUnmounted(() => {
  window.removeEventListener('language-changed', handleLanguageChange);
  window.removeEventListener('app-language-updated', handleAppLanguageUpdate);

  // Clean up map
  if (map.value) {
    map.value.remove();
  }
});

// Initialize Leaflet map
const initializeMap = () => {
  if (!mapContainer.value) return;

  // Create map centered on India
  map.value = L.map(mapContainer.value, {
    zoomControl: true,
    scrollWheelZoom: true,
    doubleClickZoom: true,
    boxZoom: true,
    keyboard: true,
    dragging: true,
    touchZoom: true
  }).setView([20.5937, 78.9629], 5); // Center of India with appropriate zoom

  // Add tile layer
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 18
  }).addTo(map.value);

  // Create a layer group for markers
  markersGroup.value = L.layerGroup().addTo(map.value);

  // Add markers for current providers
  updateMapMarkers();
};

// Update map markers when providers change
const updateMapMarkers = () => {
  if (!map.value || !markersGroup.value) return;

  // Clear existing markers
  markersGroup.value.clearLayers();
  markers.value = [];

  // Debug: Log providers to see what coordinates we have
  console.log('Providers for map:', providers.value.map(p => ({
    id: p.id,
    name: p.provider_name,
    lat: p.latitude,
    lng: p.longitude,
    parsedLat: parseFloat(p.latitude),
    parsedLng: parseFloat(p.longitude)
  })));

  const validProviders = providers.value.filter(p => {
    const lat = parseFloat(p.latitude);
    const lng = parseFloat(p.longitude);
    const isValid = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

    if (!isValid && (p.latitude || p.longitude)) {
      console.warn(`Invalid coordinates for provider ${p.id}:`, {
        lat: p.latitude,
        lng: p.longitude,
        parsedLat: lat,
        parsedLng: lng
      });
    }

    return isValid;
  });

  console.log(`Found ${validProviders.length} providers with valid coordinates out of ${providers.value.length} total providers`);

  if (validProviders.length === 0) {
    console.log('No providers with valid coordinates, keeping default India view');
    return;
  }

  // Add markers for each provider
  const bounds = [];

  validProviders.forEach(provider => {
    const lat = parseFloat(provider.latitude);
    const lng = parseFloat(provider.longitude);

    console.log(`Adding marker for ${provider.provider_name} at [${lat}, ${lng}]`);

    // Custom marker icon for different provider types
    const icon = getProviderIcon(provider.status);

    const marker = L.marker([lat, lng], { icon })
      .bindPopup(createPopupContent(provider))
      .on('click', () => highlightProviderCard(provider.id));

    markers.value.push(marker);
    markersGroup.value.addLayer(marker);
    bounds.push([lat, lng]);
  });

  // Fit map to show all markers
  if (bounds.length > 0) {
    console.log('Fitting map to bounds:', bounds);
    if (bounds.length === 1) {
      // Only one marker, center on it with reasonable zoom
      map.value.setView(bounds[0], 12);
    } else {
      // Multiple markers, fit bounds
      map.value.fitBounds(bounds, { padding: [20, 20] });
    }
  }
};

// Get custom icon based on provider status
const getProviderIcon = (status) => {
  let color = '#3B82F6'; // Default blue

  switch (status) {
    case 'verified':
      color = '#10B981'; // Green
      break;
    case 'unverified':
      color = '#F59E0B'; // Orange
      break;
    case 'raw':
      color = '#EF4444'; // Red
      break;
  }

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 25px; height: 25px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [25, 25],
    iconAnchor: [12, 12]
  });
};

// Create popup content for markers
const createPopupContent = (provider) => {
  const address = [
    provider.address_line1,
    provider.city,
    provider.state
  ].filter(Boolean).join(', ');

  return `
    <div style="min-width: 200px;">
      <h4 style="margin: 0 0 8px 0; color: #333;">${provider.provider_name}</h4>
      ${provider.provider_type ? `<p style="margin: 4px 0; color: #666; font-size: 14px;">${provider.provider_type}</p>` : ''}
      ${address ? `<p style="margin: 4px 0; color: #555; font-size: 13px;">📍 ${address}</p>` : ''}
      <div style="margin-top: 10px;">
        <button onclick="window.location.href='/providers/${provider.id}'" style="
          background: #3B82F6; 
          color: white; 
          border: none; 
          padding: 6px 12px; 
          border-radius: 4px; 
          cursor: pointer; 
          font-size: 12px;
        ">View Details</button>
      </div>
    </div>
  `;
};

// Highlight provider card when marker is clicked
const highlightProviderCard = (providerId) => {
  const cardElement = document.querySelector(`[data-provider-id="${providerId}"]`);
  if (cardElement) {
    cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    cardElement.style.transform = 'scale(1.02)';
    cardElement.style.transition = 'transform 0.3s ease';
    setTimeout(() => {
      cardElement.style.transform = 'scale(1)';
    }, 1000);
  }
};

// Watch for providers changes to update map
watch(providers, () => {
  updateMapMarkers();
}, { deep: true });

// Handler for language-changed event
const handleLanguageChange = (event) => {
  // Update the selected language from the event
  const newLanguage = event.detail.language || "english";
  if (selectedLanguage.value !== newLanguage) {
    selectedLanguage.value = newLanguage;

    // Reset to page 1 when language changes
    currentPage.value = 1;

    // Force a new search with the updated language
    searchProviders();
  }
};

// Handler for app-language-updated event (backup)
const handleAppLanguageUpdate = (event) => {
  // Update the selected language from localStorage
  const newLanguage = localStorage.getItem("preferredLanguage") || "english";
  if (selectedLanguage.value !== newLanguage) {
    selectedLanguage.value = newLanguage;

    // Reset to page 1 when language changes
    currentPage.value = 1;

    // Force a new search with the updated language
    searchProviders();
  }
};

// Load user's bookmarks using the bookmark service
const loadBookmarks = async () => {
  if (!isAuthenticated.value) return;

  try {
    isLoadingBookmarks.value = true;

    // Use the bookmark service instead of direct fetch
    const response = await fetchBookmarks(authToken.value);

    // Check if response has the expected structure
    if (response && response.bookmarks && Array.isArray(response.bookmarks)) {
      bookmarkedProviderIds.value = new Set(response.bookmarks.map(b => b.provider_id));
    } else {
      console.error('Unexpected response structure from fetchBookmarks:', response);
      // Initialize as empty set if structure is not as expected
      bookmarkedProviderIds.value = new Set();
    }
  } catch (error) {
    console.error('Error fetching bookmarks:', error);
    // Initialize as empty set on error
    bookmarkedProviderIds.value = new Set();
  } finally {
    isLoadingBookmarks.value = false;
  }
};

// Check if a provider is bookmarked
const isProviderBookmarked = (providerId) => {
  return bookmarkedProviderIds.value.has(providerId);
};

// Bookmark a provider using the bookmark service
const bookmarkProvider = async (providerId) => {
  if (!isAuthenticated.value) {
    // Redirect to login if user isn't authenticated
    router.push('/login');
    return;
  }

  try {
    await createBookmark(authToken.value, providerId);
    // Add to local set on success
    bookmarkedProviderIds.value.add(providerId);
  } catch (error) {
    // Handle "already exists" error gracefully
    if (error.message && error.message.includes("already exists")) {
      // Provider is already bookmarked, so just ensure it's in our local set
      bookmarkedProviderIds.value.add(providerId);
    } else {
      // For other errors, log them as usual
      console.error('Error bookmarking provider:', error);
    }
  }
};

// Unbookmark a provider using the bookmark service
const unbookmarkProvider = async (providerId) => {
  try {
    // Use the bookmark service instead of direct fetch
    await deleteBookmark(authToken.value, providerId);

    // Remove from local set on success
    bookmarkedProviderIds.value.delete(providerId);
  } catch (error) {
    console.error('Error unbookmarking provider:', error);
  }
};

// Check if user is admin
const isAdmin = () => {
  return currentUser.value?.role === 'admin';
};

const isValidator = () => {
  return currentUser.value?.role === 'validator';
};

// Watch for route query changes
watch(
  () => route.query,
  (newQuery) => {
    let shouldReload = false;

    if (newQuery.q !== undefined && newQuery.q !== searchQuery.value) {
      searchQuery.value = newQuery.q || "";
      shouldReload = true;
    }

    if (
      newQuery.language !== undefined &&
      newQuery.language !== selectedLanguage.value
    ) {
      selectedLanguage.value = newQuery.language || "english";
      shouldReload = true;
    }

    if (newQuery.page !== undefined) {
      const newPage = parseInt(newQuery.page) || 1;
      if (newPage !== currentPage.value) {
        currentPage.value = newPage;
        shouldReload = true;
      }
    }

    // Only reload if this watch was triggered by a navigation event
    // and not by our own searchProviders function
    if (shouldReload && (newQuery.q !== undefined || newQuery.language !== undefined || newQuery.page !== undefined)) {
      loadProviders();
    }
  }
);

// Watch for changes to the language in localStorage
watch(
  () => localStorage.getItem("preferredLanguage"),
  (newLanguage, oldLanguage) => {
    // This is a backup watcher in case the event listeners don't catch the change
    if (newLanguage && newLanguage !== oldLanguage && newLanguage !== selectedLanguage.value) {
      selectedLanguage.value = newLanguage;
      currentPage.value = 1; // Reset to page 1
      searchProviders();
    }
  }
);

const getCurrentLanguage = () => {
  return localStorage.getItem("preferredLanguage") || "english";
};

const editProvider = (provider) => {
  editingProvider.value = provider;
  showModal.value = true;
};

const onProviderSaved = (savedProvider) => {
  // Refresh the providers list
  loadProviders();
};

// Update fetchProviders service call to include pagination
const loadProviders = async () => {
  loading.value = true;
  error.value = null;

  try {
    const response = await fetchProviders(
      searchQuery.value,
      selectedLanguage.value,
      currentPage.value,
      pageSize.value
    );

    // Access the providers array from the response
    providers.value = response.providers;

    // Update pagination data
    totalItems.value = response.pagination.total;
    totalPages.value = response.pagination.total_pages;
    currentPage.value = response.pagination.page;
    pageSize.value = response.pagination.page_size;

  } catch (err) {
    console.error("Error fetching providers:", err);
    error.value = `Failed to load providers: ${err.message}`;
    providers.value = []; // Reset to empty array on error
  } finally {
    loading.value = false;
  }
};

// Handle page change
const handlePageChange = (page) => {
  currentPage.value = page;

  // Update URL with new page
  const query = { ...route.query, page };
  router.replace({ query });

  // Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' });

  // Load providers for the new page
  loadProviders();
};

// Search function that updates URL
const searchProviders = async () => {
  // Reset to page 1 when searching
  currentPage.value = 1;

  // Update URL with search parameters
  const query = {};
  if (searchQuery.value) query.q = searchQuery.value;

  // Use the current selectedLanguage value (which should be in sync with localStorage)
  query.language = selectedLanguage.value;

  // Add page to query
  query.page = currentPage.value;

  // Replace current URL with search parameters
  router.replace({ query });

  // Load providers with current search parameters
  await loadProviders();
};

const goHome = () => {
  router.push("/");
};
</script>

<template>
  <div class="providers-page">
    <!-- Main Content -->
    <main class="main-content">
      <div class="container">
        <!-- Page Header -->
        <div class="page-header">
          <h1 class="page-title">{{ dt('providers.pageTitle') }}</h1>
          <p class="page-subtitle">
            {{ dt('providers.pageSubTitle') }}
          </p>
        </div>

        <!-- Search Section -->
        <div class="search-filter-section">
          <div class="search-container">
            <NInput v-model:value="searchQuery" :placeholder="dt('home.searchPlaceholder')" class="search-input"
              size="large" @keyup.enter="searchProviders" />
            <NButton type="primary" size="large" @click="searchProviders" :loading="loading">
              🔍 {{ dt('common.search') }}
            </NButton>
          </div>
        </div>

        <!-- Results Summary -->
        <div v-if="!loading && providers.length > 0" class="results-summary">
          <p>
            {{ dt('providers.showingResults', {
              from: (currentPage - 1) * pageSize + 1,
              to: Math.min(currentPage * pageSize, totalItems),
              total: totalItems
            }) }}
          </p>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-section">
          <NSpin size="large">
            <div class="loading-text">{{ dt('providers.loading') }}...</div>
          </NSpin>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-section">
          <NAlert type="error" title="Error" closable>
            {{ error }}
            <template #action>
              <NButton size="small" @click="loadProviders">
                {{ dt('providers.tryAgain') }}
              </NButton>
            </template>
          </NAlert>
        </div>

        <!-- Results Layout with Cards and Map -->
        <div v-else-if="providers.length > 0" class="results-layout">
          <!-- Providers List on the Left -->
          <div class="providers-column">
            <div class="providers-grid">
              <ProviderCard v-for="provider in providers" :key="provider.id" :provider="provider"
                :showEditButton="isValidator()" :showBookmarkButton="isAuthenticated"
                :isBookmarked="isProviderBookmarked(provider.id)" :data-provider-id="provider.id" @edit="editProvider"
                @bookmark="bookmarkProvider" @unbookmark="unbookmarkProvider" />
            </div>
          </div>

          <!-- Map on the Right -->
          <div class="map-column">
            <div class="map-container">
              <div class="map-header">
                <h3>{{ dt('providers.mapTitle') }}</h3>
                <div class="map-legend">
                  <div class="legend-item">
                    <div class="legend-dot verified"></div>
                    <span>{{ dt('providers.verified') }}</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-dot unverified"></div>
                    <span>{{ dt('providers.unverified') }}</span>
                  </div>
                </div>
              </div>
              <div ref="mapContainer" class="map"></div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="providers-content">
          <div class="empty-state">
            <div class="empty-icon">⚖️</div>
            <h3>{{ dt('providers.emptyTitle') }}</h3>
            <p v-if="searchQuery">
              {{ dt('providers.emptySearchMessage', { query: searchQuery }) }}
            </p>
            <p v-else>
              {{ dt('providers.emptyDefaultMessage') }}
            </p>
            <div class="empty-actions">
              <NButton v-if="isAdmin()" type="success" size="large" style="margin-top: 1rem; margin-right: 1rem"
                @click="showModal = true; editingProvider = null">
                {{ dt('providers.addFirstProvider') }}
              </NButton>
              <NButton v-if="searchQuery" type="default" size="large" style="margin-top: 1rem" @click="
                searchQuery = '';
              currentPage = 1;
              loadProviders();
              ">
                {{ dt('providers.showAll') }}
              </NButton>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="!loading && providers.length > 0 && totalPages > 1" class="pagination-section">
          <NPagination v-model:page="currentPage" :page-count="totalPages" :page-size="pageSize"
            :item-count="totalItems" show-size-picker :page-sizes="[10, 20, 50, 100]" @update:page="handlePageChange"
            @update:page-size="(size) => {
              pageSize = size;
              currentPage = 1;
              searchProviders();
            }">
            <template #prefix="{ itemCount }">
              {{ dt('providers.totalProviders', { count: itemCount }) }}
            </template>
          </NPagination>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.providers-page {
  min-height: 100vh;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  background: #f8f9fa;
}

.provider-link {
  color: #3b82f6;
  text-decoration: none;
  transition: color 0.2s;
}

.provider-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-size: 2rem;
  font-weight: bold;
  color: white;
}

.nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  padding: 0.5rem 1rem;
  border-radius: 4px;
}

.nav-link:hover {
  color: #f0f0f0;
  background: rgba(255, 255, 255, 0.1);
}

.nav-link.active {
  background: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}

.main-content {
  padding: 3rem 0;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.2rem;
  color: #666;
}

.action-section {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
}

.search-filter-section {
  margin-bottom: 2rem;
}

.search-container {
  display: flex;
  max-width: 600px;
  margin: 0 auto;
  gap: 10px;
  align-items: center;
}

.search-input {
  flex: 1;
}

.results-summary {
  text-align: center;
  margin-bottom: 2rem;
  color: #666;
  font-size: 0.95rem;
}

.results-summary p {
  margin: 0;
}

.loading-section {
  text-align: center;
  padding: 4rem 0;
}

.loading-text {
  margin-top: 1rem;
  color: #666;
  font-size: 1.1rem;
}

.error-section {
  margin-bottom: 2rem;
}

/* New layout styles for cards + map */
.results-layout {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  margin-bottom: 3rem;
}

.providers-column {
  min-height: 600px;
}

.providers-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

.map-column {
  position: sticky;
  top: 20px;
  height: fit-content;
}

.map-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.map-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.map-header h3 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
}

.map-legend {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #666;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.legend-dot.verified {
  background-color: #10B981;
}

.legend-dot.unverified {
  background-color: #F59E0B;
}

.legend-dot.raw {
  background-color: #EF4444;
}

.map {
  height: 500px;
  width: 100%;
}

.provider-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.provider-card:hover {
  transform: translateY(-2px);
}

.provider-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.provider-header h3 {
  margin: 0;
  color: #333;
}

.provider-id {
  color: #666;
  font-size: 0.9rem;
  background: #f0f0f0;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.provider-info p {
  margin: 0.5rem 0;
  color: #555;
}

.provider-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.providers-content {
  background: white;
  border-radius: 10px;
  padding: 3rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  text-align: center;
  color: #666;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.empty-state p {
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}

.empty-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
  margin-top: 2rem;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Custom marker styles */
:deep(.custom-marker) {
  background: transparent !important;
  border: none !important;
}

/* Responsive design */
@media (max-width: 1024px) {
  .results-layout {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .map-column {
    position: static;
  }

  .map {
    height: 400px;
  }
}

@media (max-width: 768px) {
  .header .container {
    flex-direction: column;
    gap: 1rem;
  }

  .nav {
    gap: 1rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .action-section {
    flex-direction: column;
    align-items: center;
  }

  .search-container {
    flex-direction: column;
    max-width: 100%;
  }

  .providers-grid {
    grid-template-columns: 1fr;
  }

  .provider-actions {
    justify-content: center;
  }

  .empty-actions {
    flex-direction: column;
    align-items: center;
  }

  .pagination-section {
    padding: 1rem 0;
  }

  .map-legend {
    flex-direction: column;
    gap: 0.5rem;
  }

  .map {
    height: 300px;
  }
}
</style>